import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';

const CABLE_COLOR_HEX = {
  BLUE: '#3B82F6', BROWN: '#8B4513', 'GREEN-YELLOW': '#9ACD32', GREY: '#808080',
  BLACK: '#333333', WHITE: '#EEEEEE', RED: '#EF4444', YELLOW: '#F59E0B',
  GREEN: '#22C55E', ORANGE: '#F97316'
};
const COPPER = '#B87333';
const STEEL = '#9AA0A6';

const insulationHex = c => CABLE_COLOR_HEX[String(c || '').toUpperCase()] || '#D9D2C5';
const sheathHex = (type, productColor) =>
  CABLE_COLOR_HEX[String(productColor || '').toUpperCase()] ||
  (String(type || '').toUpperCase().includes('M1') ? '#C9C4BC' : '#6b7280');

// a flat filled disc (conductor) facing +Z
function Disc({ r, z = 0, color, offset = [0, 0], metalness = 0.1, roughness = 0.6 }) {
  return (
    <mesh position={[offset[0], offset[1], z]}>
      <circleGeometry args={[r, 48]} />
      <meshStandardMaterial color={color} metalness={metalness} roughness={roughness} />
    </mesh>
  );
}

// a flat annulus (a layer ring) facing +Z
function Ring({ inner, outer, z = 0, color, offset = [0, 0], metalness = 0.1, roughness = 0.7 }) {
  if (!(outer > inner)) return null;
  return (
    <mesh position={[offset[0], offset[1], z]}>
      <ringGeometry args={[inner, outer, 64]} />
      <meshStandardMaterial color={color} metalness={metalness} roughness={roughness} side={2} />
    </mesh>
  );
}

// One insulated core = copper disc + insulation ring. Eccentric: copper is
// shifted toward the thin side; the insulation ring is drawn as the outer disc
// minus the (offset) copper, faked by an outer insulation disc + copper on top.
function Core({ model, at = [0, 0], eccentric, z = 0 }) {
  const copperR = model.copperD / 2;
  const outerR = model.outerD / 2;
  const off = eccentric ? model.eccentricOffset : 0; // shift copper toward thin side (+x)
  return (
    <group position={[at[0], at[1], 0]}>
      {/* full insulation disc */}
      <Disc r={outerR} z={z} color={insulationHex(model.color)} roughness={0.75} />
      {/* copper on top, offset toward thin side */}
      <Disc r={copperR} z={z + 0.02} color={COPPER} offset={[off, 0]} metalness={0.85} roughness={0.35} />
    </group>
  );
}

const Scene = ({ model, eccentric }) => {
  const cores = model.cores || [];
  // physical conductor count = the modeled cores (a reduced green-yellow cable like
  // "3x25+16" has numberOfConductors=3 but 4 actual cores → render all 4).
  const n = Math.max(cores.length || 0, model.numberOfConductors || 1);
  const ropeR = (model.ropeDiameter || model.coreOuterD) / 2;
  const coreOuterR = (model.coreOuterD || 0) / 2;
  // pitch radius: fit inside the rope, but never so tight the cores overlap — a
  // reduced-GY cable (4 cores in a "3x35" rope) would otherwise collide.
  const pitchR = n <= 1 ? 0 : Math.max(ropeR - coreOuterR, coreOuterR / Math.sin(Math.PI / n));
  const displayRopeR = Math.max(ropeR, pitchR + coreOuterR); // filler ring must contain the cores

  // core centers on a pitch circle (single core → centered)
  const centers = useMemo(() => {
    if (n <= 1) return [[0, 0]];
    return Array.from({ length: n }, (_, i) => {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      return [Math.cos(a) * pitchR, Math.sin(a) * pitchR];
    });
  }, [n, pitchR]);

  return (
    <group>
      {/* cable-level layers, drawn back-to-front (outermost first, deepest z) */}
      {model.cableLayers
        .slice()
        .reverse()
        .map((L, i) => {
          const z = -0.1 - i * 0.05;
          if (L.kind === 'armour') {
            return <Ring key={L.key} inner={L.innerD / 2} outer={L.outerD / 2} z={z} color={STEEL} metalness={0.7} roughness={0.4} />;
          }
          return (
            <Ring
              key={L.key}
              inner={L.innerD / 2}
              outer={L.outerD / 2}
              z={z}
              color={sheathHex(L.type, L.name === 'exterior_sheat' ? model.color : null)}
            />
          );
        })}

      {/* rope filler ring (between cores envelope and interior sheath inner) */}
      {n > 1 && (
        <mesh position={[0, 0, -0.05]}>
          <circleGeometry args={[displayRopeR, 48]} />
          <meshStandardMaterial color="#E7E2D8" roughness={0.85} />
        </mesh>
      )}

      {/* cores on top */}
      {centers.map((c, i) => (
        <Core key={i} model={cores[Math.min(i, cores.length - 1)] || cores[0]} at={c} eccentric={eccentric} z={0} />
      ))}
    </group>
  );
};

// a stadium / rounded-rectangle THREE.Shape (fully-rounded short ends) for flat cables
function stadiumShape(width, height) {
  const w = Math.max(width, height);
  const h = height;
  const r = Math.min(h / 2, w / 2);
  const shape = new THREE.Shape();
  const x = -w / 2;
  const y = -h / 2;
  shape.moveTo(x + r, y);
  shape.lineTo(x + w - r, y);
  shape.absarc(x + w - r, y + r, r, -Math.PI / 2, Math.PI / 2, false);
  shape.lineTo(x + r, y + h);
  shape.absarc(x + r, y + r, r, Math.PI / 2, Math.PI * 1.5, false);
  shape.closePath();
  return shape;
}

// sheath wall of a flat cable's outer sheath (spec avg, not the misleading round Ø)
const flatWall = (model, coreD) => {
  const sheath = [...(model.cableLayers || [])].reverse().find(L => L.kind === 'sheath');
  const avg = Number(sheath?.wall?.avgSpec);
  if (avg > 0) return avg;
  if (sheath) return Math.max(0.2, (sheath.outerD - sheath.innerD) / 2);
  return coreD * 0.35;
};

// Flat cable (e.g. MYYUP): conductors laid side-by-side in a row inside a
// rectangular (stadium) sheath — no rope, no round envelope.
const FlatScene = ({ model }) => {
  const cores = model.cores || [];
  const n = model.numberOfConductors || cores.length || 1;
  const coreD = model.coreOuterD || cores[0]?.outerD || 1;
  const wall = flatWall(model, coreD);
  const width = n * coreD + 2 * wall;
  const heightRect = coreD + 2 * wall;
  const shape = useMemo(() => stadiumShape(width, heightRect), [width, heightRect]);
  const sheath = [...(model.cableLayers || [])].reverse().find(L => L.kind === 'sheath');
  const sheathColor = sheathHex(sheath?.type, model.color);
  const centers = Array.from({ length: n }, (_, i) => [(i - (n - 1) / 2) * coreD, 0]);
  return (
    <group>
      <mesh position={[0, 0, -0.12]}>
        <shapeGeometry args={[shape]} />
        <meshStandardMaterial color={sheathColor} roughness={0.75} side={2} />
      </mesh>
      {centers.map((c, i) => (
        <Core key={i} model={cores[Math.min(i, cores.length - 1)] || cores[0]} at={c} eccentric={false} z={0} />
      ))}
    </group>
  );
};

const CableCrossSection3D = ({ model, eccentric = false, height = 420 }) => {
  if (!model || !model.cores?.length) {
    return (
      <div style={{ height, background: '#0f1420', borderRadius: 6 }}
        className="d-flex align-items-center justify-content-center text-500">
        Search a standard to render its cross-section
      </div>
    );
  }
  const n = model.numberOfConductors || model.cores.length || 1;
  const isFlat = !model.hasRope && n > 1; // no rope + multi-core → laid flat (MYYUP)
  const coreD = model.coreOuterD || 1;
  const R = isFlat ? (n * coreD + 2 * flatWall(model, coreD)) / 2 : (model.outerDiameter || 10) / 2;
  const cam = R * 3.2;

  return (
    <div style={{ height, background: '#0f1420', borderRadius: 6, overflow: 'hidden' }}>
      <Canvas camera={{ position: [0, 0, cam], fov: 40 }} dpr={[1, 2]}>
        <color attach="background" args={['#0f1420']} />
        <ambientLight intensity={0.85} />
        <directionalLight position={[R * 2, R * 3, R * 6]} intensity={1.1} />
        <directionalLight position={[-R * 3, -R * 2, R * 4]} intensity={0.4} />
        <OrbitControls enablePan enableZoom />
        {isFlat ? <FlatScene model={model} /> : <Scene model={model} eccentric={eccentric} />}
        <Html position={[0, -R * 1.2, 0]} center>
          <div style={{ color: '#9fb3c8', fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
            {model.name} {model.numberOfConductors}x{model.section}
            {model.greenYellowSection ? `+${model.greenYellowSection}` : ''}
            {isFlat
              ? ' · flat cable (rectangular sheath)'
              : ` · outer Ø ${model.outerDiameter.toFixed(2)} mm${eccentric ? ' · eccentric' : ' · concentric'}`}
          </div>
        </Html>
      </Canvas>
    </div>
  );
};

export default CableCrossSection3D;
